import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, Trash2, Plus, X, GripVertical, MoreVertical, Edit2 } from 'lucide-react';
import { FiUser, FiZap, FiCheckCircle, FiLoader, FiClock, FiCircle, FiCalendar, FiFlag, FiUserPlus, FiTrash2, FiX, FiCheckSquare, FiSquare, FiMinusSquare, FiCopy } from 'react-icons/fi';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useAuth } from '../hooks/useAuth';
import { useTask } from './taskManager/contexts/TaskContext';
// FIREBASE MODE (reverted from Supabase)
import { updateTask, deleteTask, createTask, duplicateTask, addSubtask, updateSubtask, deleteSubtask, createSection, updateSection, deleteSection, bulkUpdateTasks, bulkDeleteTasks, updateList, getListDetails } from './taskManager/services/firestore';
import StatusTag from '../components/ui/StatusTag';
import StatusPopover from './taskManager/components/StatusPopover';
import DatePickerPopover from './taskManager/components/DatePickerPopover';
import UserSelectPopover from './taskManager/components/UserSelectPopover';
import AddColumnPopover from './taskManager/components/AddColumnPopover';
import ColumnOptionsPopover from './taskManager/components/ColumnOptionsPopover';

import toast from 'react-hot-toast';
import TaskContextMenu from './taskManager/components/TaskContextMenu';
import AIPopover from './taskManager/components/AIPopover';
import DeleteConfirmationModal from './taskManager/components/DeleteConfirmationModal';

// Inline editable task name (like Asana/MyTasks)
const InlineTaskName = ({ titulo, isCompleted, onSave, onClick, onContextMenu }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(titulo);
  const inputRef = useRef(null);

  useEffect(() => { setValue(titulo); }, [titulo]);
  useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);

  const handleBlur = () => {
    setEditing(false);
    const trimmed = value.trim();
    if (trimmed && trimmed !== titulo) onSave(trimmed);
    else setValue(titulo);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); inputRef.current?.blur(); }
          if (e.key === 'Escape') { setValue(titulo); setEditing(false); }
        }}
        onClick={(e) => e.stopPropagation()}
        className="w-full text-[13px] bg-white border border-blue-400 rounded px-1.5 py-0.5 outline-none ring-2 ring-blue-100 text-gray-900 flex-1"
      />
    );
  }

  return (
    <>
      <span
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        onContextMenu={onContextMenu}
        className={`text-[13px] cursor-text truncate transition-colors hover:bg-gray-100 rounded px-1 py-0.5
          ${isCompleted ? 'text-gray-400 line-through' : 'text-gray-800'}`}
      >
        {titulo}
      </span>
      <div className="flex-1 min-w-[20px]" onContextMenu={onContextMenu} />
    </>
  );
};


const EditableCell = ({ task, column, isSubtask, parentId, user, selectedProjectId, selectedFolderId, selectedListId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(task[column.id] || '');

  useEffect(() => {
    setValue(task[column.id] || '');
  }, [task, column.id]);

  const handleSave = async () => {
    setIsEditing(false);
    if (value === task[column.id]) return;

    try {
      if (isSubtask) {
        await updateSubtask(user.uid, selectedProjectId, selectedFolderId, selectedListId, parentId, task.id, {
          [column.id]: value
        });
      } else {
        await updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, task.id, {
          [column.id]: value
        });
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al guardar');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') setIsEditing(false);
  };

  if (isEditing) {
    return (
      <input
        type={column.type === 'number' ? 'number' : column.type === 'date' ? 'date' : 'text'}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        autoFocus
        className="w-full px-2 py-1 text-sm border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
        placeholder={column.title}
      />
    );
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      className="w-full h-full min-h-[28px] flex items-center px-2 cursor-text hover:bg-gray-50 rounded text-sm text-gray-700 truncate"
    >
      {value || <span className="text-gray-300 text-xs">--</span>}
    </div>
  );
};

// Asana-style inline task add row
const InlineTaskAdd = ({ sectionId, onSave }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [taskName, setTaskName] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  const handleSave = () => {
    if (!taskName.trim()) {
      setIsAdding(false);
      return;
    }
    onSave({
      titulo: taskName.trim(),
      estado: 'planificacion',
      seccionId: sectionId,
    });
    setTaskName('');
    // Keep open for rapid entry
    if (inputRef.current) inputRef.current.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setTaskName('');
      setIsAdding(false);
    }
  };

  if (!isAdding) {
    return (
      <tr
        onClick={() => setIsAdding(true)}
        className="group cursor-pointer"
      >
        <td colSpan={100} className="px-0 py-0">
          <div className="flex items-center gap-2 px-10 py-[7px] text-gray-400 hover:text-gray-600 transition-colors border-b border-transparent hover:bg-gray-50/50">
            <Plus size={14} className="text-gray-400 group-hover:text-blue-500 transition-colors" />
            <span className="text-[13px]">Agregar tarea...</span>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="bg-white">
      <td colSpan={100} className="px-0 py-0">
        <div className="flex items-center gap-2 pl-10 pr-4 py-[5px] border-b border-blue-200 bg-blue-50/30">
          <div className="w-[18px] h-[18px] rounded-full border-2 border-gray-300 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={taskName}
            onChange={(e) => setTaskName(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              if (!taskName.trim()) setIsAdding(false);
              else handleSave();
            }}
            placeholder="Escribe el nombre de la tarea y presiona Enter"
            className="flex-1 text-[13px] bg-transparent border-none focus:outline-none focus:ring-0 placeholder-gray-400 text-gray-800 py-1"
          />
          <span className="text-[11px] text-gray-400 flex-shrink-0 hidden sm:block">Enter para guardar, Esc para cancelar</span>
        </div>
      </td>
    </tr>
  );
};


const TaskListViewImproved = ({
  tasks = [],
  selectedProjectId,
  selectedFolderId,
  selectedListId,
  onTaskClick,
  setShowTaskDrawer,
  setSelectedTaskId,
  viewType,
}) => {
  const { user } = useAuth();
  const { users, sections, loadingSections, setSelectedSubtaskPath } = useTask();

  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedTasks, setExpandedTasks] = useState({});
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null);
  const [newTaskName, setNewTaskName] = useState('');
  const [showNewTask, setShowNewTask] = useState(null);

  // Popover states
  const [statusPopoverTask, setStatusPopoverTask] = useState(null);
  const [statusPopoverPos, setStatusPopoverPos] = useState(null);
  const [datePopoverTask, setDatePopoverTask] = useState(null);
  const [datePopoverPos, setDatePopoverPos] = useState(null);
  const [userPopoverTask, setUserPopoverTask] = useState(null);
  const [userPopoverPos, setUserPopoverPos] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [aiPopover, setAiPopover] = useState(null);

  // Column State
  const DEFAULT_COLUMNS = [
    { id: 'name', title: 'Nombre de tarea', width: 450, type: 'name' },
    { id: 'assignee', title: 'Responsable', width: 170, type: 'user' },
    { id: 'date', title: 'Fecha límite', width: 140, type: 'date' },
    { id: 'status', title: 'Estado', width: 140, type: 'status' },
  ];
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [showAddColumn, setShowAddColumn] = useState(null);
  const [columnOptions, setColumnOptions] = useState(null);

  // Load custom columns from list document
  useEffect(() => {
    if (!user?.uid || !selectedListId) return;
    getListDetails(user.uid, selectedListId).then(listData => {
      if (listData?.customColumns) {
        setColumns(listData.customColumns);
      } else {
        setColumns(DEFAULT_COLUMNS);
      }
    }).catch(() => {});
  }, [user?.uid, selectedListId]);

  // Persist columns to Firestore when they change
  const columnsKey = useRef(null);
  useEffect(() => {
    const key = JSON.stringify(columns);
    if (columnsKey.current === null) { columnsKey.current = key; return; }
    if (key === columnsKey.current) return;
    columnsKey.current = key;
    if (!user?.uid || !selectedProjectId || !selectedFolderId || !selectedListId) return;
    const timer = setTimeout(() => {
      updateList(user.uid, selectedProjectId, selectedFolderId, selectedListId, { customColumns: columns }).catch(() => {});
    }, 500);
    return () => clearTimeout(timer);
  }, [columns, user?.uid, selectedProjectId, selectedFolderId, selectedListId]);

  // Section States
  const [showNewSection, setShowNewSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingSectionName, setEditingSectionName] = useState('');
  const [sectionMenuOpen, setSectionMenuOpen] = useState(null);

  // Subtask States
  const [addingSubtaskFor, setAddingSubtaskFor] = useState(null);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');

  // Bulk actions state
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkPriorityOpen, setBulkPriorityOpen] = useState(false);
  const [bulkAssigneeOpen, setBulkAssigneeOpen] = useState(false);
  const [bulkDateOpen, setBulkDateOpen] = useState(false);

  // Resize State
  const [resizingColumn, setResizingColumn] = useState(null);
  const resizeRef = useRef(null);

  const handleContextMenu = (e, task, isSubtask = false) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, task, isSubtask });
  };

  const handleContextMenuAction = async (action, task) => {
    switch (action) {
      case 'open':
        setSelectedTaskId(task.id);
        if (contextMenu?.isSubtask) {
          const parent = tasks.find(t => t.subtareas && t.subtareas.some(s => s.id === task.id));
          if (parent) {
            setSelectedTaskId(parent.id);
            setSelectedSubtaskPath([task.id]);
          }
        } else {
          setSelectedSubtaskPath([]);
        }
        setShowTaskDrawer(true);
        break;
      case 'open_new_tab':
        window.open(window.location.href, '_blank');
        break;
      case 'duplicate':
        if (contextMenu?.isSubtask) {
          toast('Duplicar subtarea próximamente');
        } else {
          try {
            await duplicateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, task);
            toast.success(`Tarea duplicada con ${task.subtareas?.length || 0} subtareas`);
          } catch (e) {
            console.error(e);
            toast.error('Error al duplicar');
          }
        }
        break;
      case 'add_subtask':
        if (contextMenu?.isSubtask) {
          toast('No se puede agregar subtarea a una subtarea');
        } else {
          setExpandedTasks(prev => ({ ...prev, [task.id]: true }));
          setAddingSubtaskFor(task.id);
        }
        break;
      case 'follow_up':
        toast('Función de seguimiento próximamente');
        break;
      case 'delete':
        if (contextMenu?.isSubtask) {
          const parent = tasks.find(t => t.subtareas && t.subtareas.some(s => s.id === task.id));
          if (parent) handleDeleteSubtask(parent.id, task.id);
        } else {
          handleDeleteTask(task.id);
        }
        break;
      case 'toggle_status':
        const newStatus = task.estado === 'finalizado' ? 'en-curso' : 'finalizado';
        if (contextMenu?.isSubtask) {
          const parent = tasks.find(t => t.subtareas && t.subtareas.some(s => s.id === task.id));
          if (parent) handleStatusChange({ taskId: task.id, isSubtask: true, parentId: parent.id }, newStatus);
        } else {
          handleStatusChange({ taskId: task.id, isSubtask: false }, newStatus);
        }
        break;
      case 'ai':
        setAiPopover({
          taskId: contextMenu?.isSubtask ? tasks.find(t => t.subtareas && t.subtareas.some(s => s.id === task.id))?.id : task.id,
          subtaskId: contextMenu?.isSubtask ? task.id : null,
          initialContent: task.titulo,
          isSubtask: contextMenu?.isSubtask,
          position: { top: window.innerHeight / 2 - 200, left: window.innerWidth / 2 - 200 }
        });
        break;
      case 'copy_link':
        navigator.clipboard.writeText(window.location.href);
        toast.success('Enlace copiado');
        break;
      default:
        break;
    }
  };

  const handleColumnResizeStart = (e, columnId) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = columns.find(c => c.id === columnId)?.width || 100;

    setResizingColumn(columnId);
    resizeRef.current = { startX, startWidth, columnId };

    const overlay = document.createElement('div');
    overlay.id = 'resize-overlay';
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:9999;cursor:col-resize';
    document.body.appendChild(overlay);

    const handleMouseMove = (moveEvent) => {
      if (!resizeRef.current) return;
      requestAnimationFrame(() => {
        const diff = moveEvent.clientX - resizeRef.current.startX;
        const newWidth = Math.max(100, Math.min(800, resizeRef.current.startWidth + diff));
        setColumns(prev => prev.map(col =>
          col.id === resizeRef.current.columnId ? { ...col, width: newWidth } : col
        ));
      });
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
      resizeRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      const el = document.getElementById('resize-overlay');
      if (el) el.remove();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleColumnAction = (action, column) => {
    switch (action) {
      case 'delete':
        setDeleteModal({
          isOpen: true, type: 'task',
          title: `¿Eliminar columna "${column.title}"?`,
          message: 'La columna será eliminada de la vista.',
          onConfirm: () => {
            setColumns(prev => prev.filter(c => c.id !== column.id));
            toast.success('Columna eliminada');
          }
        });
        break;
      case 'hide':
        setColumns(prev => prev.filter(c => c.id !== column.id));
        toast.success('Columna oculta');
        break;
      case 'move_start':
        setColumns(prev => { const newCols = prev.filter(c => c.id !== column.id); return [column, ...newCols]; });
        break;
      case 'move_end':
        setColumns(prev => {
          const newCols = prev.filter(c => c.id !== column.id);
          const actionsIndex = newCols.findIndex(c => c.id === 'actions');
          if (actionsIndex !== -1) { newCols.splice(actionsIndex, 0, column); return newCols; }
          return [...newCols, column];
        });
        break;
      case 'duplicate':
        const newCol = { ...column, id: `col_${Date.now()}`, title: `${column.title} (Copia)` };
        setColumns(prev => {
          const idx = prev.findIndex(c => c.id === column.id);
          const newCols = [...prev];
          newCols.splice(idx + 1, 0, newCol);
          return newCols;
        });
        toast.success('Columna duplicada');
        break;
      default:
        toast('Función próximamente');
    }
  };

  const handleAddColumn = (type) => {
    const newColumn = { id: `col_${Date.now()}`, title: type.label, width: 150, type: type.id };
    setColumns(prev => {
      const actionsIndex = prev.findIndex(c => c.id === 'actions');
      const newCols = [...prev];
      newCols.splice(actionsIndex, 0, newColumn);
      return newCols;
    });
    setShowAddColumn(null);
    toast.success(`Columna ${type.label} agregada`);
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const toggleTask = (taskId) => {
    setExpandedTasks((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = typeof date === 'object' && date.seconds ? new Date(date.seconds * 1000) : new Date(date);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const isOverdue = d < now && d.toDateString() !== now.toDateString();
    const isToday = d.toDateString() === now.toDateString();
    const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
    const isTomorrow = d.toDateString() === tomorrow.toDateString();

    if (isToday) return 'Hoy';
    if (isTomorrow) return 'Mañana';
    return d.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
  };

  const getDateColor = (date) => {
    if (!date) return 'text-gray-400';
    const d = typeof date === 'object' && date.seconds ? new Date(date.seconds * 1000) : new Date(date);
    if (isNaN(d.getTime())) return 'text-gray-400';
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    if (d < now) return 'text-red-500';
    if (d.toDateString() === new Date().toDateString()) return 'text-green-600';
    return 'text-gray-600';
  };

  const handleSelectTask = (taskId) => {
    const newSelection = new Set(selectedTasks);
    if (newSelection.has(taskId)) newSelection.delete(taskId);
    else newSelection.add(taskId);
    setSelectedTasks(newSelection);
  };

  const handleSelectAll = (sectionId) => {
    const groupTaskIds = tasks
      .filter((t) => t.seccionId === sectionId || (!t.seccionId && sections.find(s => s.id === sectionId)?.nombre === 'PLANIFICACIÓN'))
      .map((t) => t.id) || [];
    const allSelected = groupTaskIds.every((id) => selectedTasks.has(id));
    const newSelection = new Set(selectedTasks);
    if (allSelected) groupTaskIds.forEach((id) => newSelection.delete(id));
    else groupTaskIds.forEach((id) => newSelection.add(id));
    setSelectedTasks(newSelection);
  };

  const handleSelectAllTasks = () => {
    const allIds = tasks.map(t => t.id);
    const allSelected = allIds.length > 0 && allIds.every(id => selectedTasks.has(id));
    if (allSelected) setSelectedTasks(new Set());
    else setSelectedTasks(new Set(allIds));
  };

  // Bulk Actions
  const handleBulkStatusChange = async (newStatus) => {
    const ids = [...selectedTasks];
    try {
      await bulkUpdateTasks(user.uid, selectedProjectId, selectedFolderId, selectedListId, ids, { estado: newStatus });
      toast.success(`${ids.length} tareas actualizadas`);
      setSelectedTasks(new Set());
      setBulkStatusOpen(false);
    } catch (error) { toast.error('Error al actualizar tareas'); }
  };

  const handleBulkPriorityChange = async (newPriority) => {
    const ids = [...selectedTasks];
    try {
      await bulkUpdateTasks(user.uid, selectedProjectId, selectedFolderId, selectedListId, ids, { prioridad: newPriority });
      toast.success(`${ids.length} tareas actualizadas`);
      setSelectedTasks(new Set());
      setBulkPriorityOpen(false);
    } catch (error) { toast.error('Error al actualizar tareas'); }
  };

  const handleBulkAssigneeChange = async (email) => {
    const ids = [...selectedTasks];
    try {
      await bulkUpdateTasks(user.uid, selectedProjectId, selectedFolderId, selectedListId, ids, { asignados: email ? [email] : [] });
      toast.success(`${ids.length} tareas asignadas`);
      setSelectedTasks(new Set());
      setBulkAssigneeOpen(false);
    } catch (error) { toast.error('Error al asignar tareas'); }
  };

  const handleBulkDateChange = async (newDate) => {
    const ids = [...selectedTasks];
    try {
      await bulkUpdateTasks(user.uid, selectedProjectId, selectedFolderId, selectedListId, ids, { fechaVencimiento: newDate });
      toast.success(`${ids.length} tareas actualizadas`);
      setSelectedTasks(new Set());
      setBulkDateOpen(false);
    } catch (error) { toast.error('Error al actualizar fechas'); }
  };

  const handleBulkDuplicate = async () => {
    const ids = [...selectedTasks];
    const tasksToDuplicate = tasks.filter(t => ids.includes(t.id));
    if (tasksToDuplicate.length === 0) return;
    try {
      for (const task of tasksToDuplicate) {
        await duplicateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, task);
      }
      const totalSubtasks = tasksToDuplicate.reduce((sum, t) => sum + (t.subtareas?.length || 0), 0);
      toast.success(`${tasksToDuplicate.length} tarea${tasksToDuplicate.length > 1 ? 's' : ''} duplicada${tasksToDuplicate.length > 1 ? 's' : ''} con ${totalSubtasks} subtarea${totalSubtasks !== 1 ? 's' : ''}`);
      setSelectedTasks(new Set());
    } catch (error) {
      console.error(error);
      toast.error('Error al duplicar tareas');
    }
  };

  const handleBulkDelete = () => {
    const ids = [...selectedTasks];
    setDeleteModal({
      isOpen: true,
      title: `Eliminar ${ids.length} tarea${ids.length > 1 ? 's' : ''}`,
      message: `¿Estás seguro de eliminar ${ids.length} tarea${ids.length > 1 ? 's' : ''} seleccionada${ids.length > 1 ? 's' : ''}? Esta acción no se puede deshacer.`,
      type: 'task',
      onConfirm: async () => {
        try {
          await bulkDeleteTasks(user.uid, selectedProjectId, selectedFolderId, selectedListId, ids);
          toast.success(`${ids.length} tareas eliminadas`);
          setSelectedTasks(new Set());
        } catch (error) { toast.error('Error al eliminar tareas'); }
      }
    });
  };

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId, type } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (type === 'COLUMN') {
      const newColumns = Array.from(columns);
      const [removed] = newColumns.splice(source.index, 1);
      newColumns.splice(destination.index, 0, removed);
      setColumns(newColumns);
      return;
    }

    if (type === 'SUBTASK') {
      const sourceTaskId = source.droppableId.replace('subtasks-', '');
      const destTaskId = destination.droppableId.replace('subtasks-', '');

      if (sourceTaskId === destTaskId) {
        const task = tasks.find(t => t.id === sourceTaskId);
        if (!task || !task.subtareas) return;
        const newSubtasks = Array.from(task.subtareas);
        const [movedSubtask] = newSubtasks.splice(source.index, 1);
        newSubtasks.splice(destination.index, 0, movedSubtask);
        try {
          await updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, sourceTaskId, { subtareas: newSubtasks });
        } catch (error) { toast.error('Error al reordenar subtarea'); }
      } else {
        const sourceTask = tasks.find(t => t.id === sourceTaskId);
        const destTask = tasks.find(t => t.id === destTaskId);
        if (!sourceTask || !destTask) return;
        const sourceSubtasks = Array.from(sourceTask.subtareas || []);
        const destSubtasks = Array.from(destTask.subtareas || []);
        const [movedSubtask] = sourceSubtasks.splice(source.index, 1);
        destSubtasks.splice(destination.index, 0, movedSubtask);
        try {
          await Promise.all([
            updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, sourceTaskId, { subtareas: sourceSubtasks }),
            updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, destTaskId, { subtareas: destSubtasks })
          ]);
          toast.success('Subtarea movida');
        } catch (error) { toast.error('Error al mover subtarea'); }
      }
      return;
    }

    const newSectionId = destination.droppableId;
    try {
      await updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, draggableId, { seccionId: newSectionId });
    } catch (error) { toast.error('Error al mover tarea'); }
  };

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false, type: null, title: '', message: '', itemData: null
  });

  const confirmDelete = async () => {
    const { type, itemData, onConfirm } = deleteModal;
    if (onConfirm) {
      await onConfirm();
    } else if (type === 'task') {
      try {
        await deleteTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, itemData.taskId);
        toast.success('Tarea eliminada');
      } catch (error) { toast.error('Error al eliminar tarea'); }
    } else if (type === 'subtask') {
      try {
        await deleteSubtask(user.uid, selectedProjectId, selectedFolderId, selectedListId, itemData.parentId, itemData.subtaskId);
        toast.success('Subtarea eliminada');
      } catch (error) { toast.error('Error al eliminar subtarea'); }
    }
    setDeleteModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleDeleteTask = (taskId) => {
    setDeleteModal({
      isOpen: true, type: 'task', title: '¿Eliminar tarea?',
      message: 'Esta acción eliminará la tarea y todas sus subtareas permanentemente.',
      itemData: { taskId }
    });
  };

  const handleStatusChange = async (target, newStatus) => {
    try {
      if (target.isSubtask) {
        await updateSubtask(user.uid, selectedProjectId, selectedFolderId, selectedListId, target.parentId, target.taskId, { estado: newStatus });
      } else {
        await updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, target.taskId, { estado: newStatus });
      }
      setEditingCell(null);
    } catch (error) {
      console.error(error);
      toast.error('Error al actualizar');
    }
  };

  const handleDateChange = async (target, newDate) => {
    try {
      const dateStr = newDate ? new Date(newDate).toISOString() : null;
      if (target.isSubtask) {
        await updateSubtask(user.uid, selectedProjectId, selectedFolderId, selectedListId, target.parentId, target.taskId, { fechaVencimiento: dateStr });
      } else {
        await updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, target.taskId, { fechaVencimiento: dateStr });
      }
      setEditingCell(null);
    } catch (error) {
      console.error(error);
      toast.error('Error al actualizar');
    }
  };

  const handleAssigneeChange = async (target, email) => {
    try {
      if (target.isSubtask) {
        await updateSubtask(user.uid, selectedProjectId, selectedFolderId, selectedListId, target.parentId, target.taskId, { asignados: email ? [email] : [] });
      } else {
        await updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, target.taskId, { asignados: email ? [email] : [] });
      }
      setEditingCell(null);
    } catch (error) {
      console.error(error);
      toast.error('Error al actualizar');
    }
  };

  const handleAddTask = async (groupKey) => {
    if (!newTaskName.trim()) return;
    try {
      await createTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, {
        titulo: newTaskName, estado: groupKey,
      });
      toast.success('Tarea creada');
      setNewTaskName('');
      setShowNewTask(null);
    } catch (error) { toast.error('Error al crear tarea'); }
  };

  const handleAddSubtask = async (parentId) => {
    setAddingSubtaskFor(parentId);
    setNewSubtaskTitle('');
    if (!expandedTasks[parentId]) toggleTask(parentId);
  };

  const handleSaveSubtask = async (parentId) => {
    if (!newSubtaskTitle.trim()) return;
    try {
      await addSubtask(user.uid, selectedProjectId, selectedFolderId, selectedListId, parentId, {
        titulo: newSubtaskTitle.trim(), estado: 'planificacion'
      });
      toast.success('Subtarea creada');
      setNewSubtaskTitle('');
      setAddingSubtaskFor(null);
    } catch (error) { toast.error('Error al crear subtarea'); }
  };

  const handleDeleteSubtask = (parentId, subtaskId) => {
    setDeleteModal({
      isOpen: true, type: 'subtask', title: '¿Eliminar subtarea?',
      message: 'Esta acción eliminará la subtarea permanentemente.',
      itemData: { parentId, subtaskId }
    });
  };

  // Agrupar tareas por seccionId
  const groupedTasks = sections.reduce((acc, section) => {
    acc[section.id] = tasks.filter((t) => t.seccionId === section.id || (!t.seccionId && section.nombre === 'PLANIFICACIÓN'));
    return acc;
  }, {});

  // ==================== RENDER ====================

  // Completion circle component (Asana-style)
  const CompletionCircle = ({ estado, onClick, size = 18 }) => {
    const isComplete = estado === 'finalizado';
    const isInProgress = estado === 'en-curso';

    return (
      <button
        onClick={onClick}
        className={`group/check flex-shrink-0 rounded-full border-2 transition-all duration-200 flex items-center justify-center
          ${isComplete
            ? 'bg-green-500 border-green-500 text-white'
            : isInProgress
              ? 'border-blue-400 hover:border-blue-500 bg-white hover:bg-blue-50'
              : 'border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50'
          }`}
        style={{ width: size, height: size }}
      >
        {isComplete && (
          <svg width={size - 6} height={size - 6} viewBox="0 0 12 12" fill="none">
            <path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        {isInProgress && (
          <div className="w-2 h-2 rounded-full bg-blue-400" />
        )}
        {!isComplete && !isInProgress && (
          <svg width={size - 6} height={size - 6} viewBox="0 0 12 12" fill="none" className="opacity-0 group-hover/check:opacity-100 transition-opacity">
            <path d="M2.5 6L5 8.5L9.5 4" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
    );
  };

  // Assignee avatar render
  const renderAssignee = (asignados, onClick) => {
    if (!asignados || asignados.length === 0) {
      return (
        <div
          onClick={onClick}
          className="w-7 h-7 rounded-full border border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-all"
        >
          <FiUser size={13} className="text-gray-400" />
        </div>
      );
    }

    const email = asignados[0];
    const assignedUser = users.find(u => u.email === email);
    const isCurrentUser = user && user.email === email;
    const displayName = isCurrentUser ? (user.displayName || assignedUser?.displayName || email) : (assignedUser?.displayName || email);
    const photoURL = isCurrentUser ? (user.photoURL || assignedUser?.photoURL) : assignedUser?.photoURL;

    return (
      <div onClick={onClick} className="flex items-center gap-2 cursor-pointer group/assignee">
        <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-white" title={displayName}>
          {photoURL ? (
            <img src={photoURL} alt={displayName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-purple-500 to-indigo-600 text-white text-xs font-medium flex items-center justify-center">
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <span className="text-[13px] text-gray-600 group-hover/assignee:text-blue-600 truncate transition-colors hidden lg:block">
          {displayName.split(' ')[0]}
          {asignados.length > 1 && <span className="text-gray-400 ml-1">+{asignados.length - 1}</span>}
        </span>
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col bg-white transition-colors h-full overflow-hidden">
      <div className="flex-1 overflow-auto h-full">
        <DragDropContext onDragEnd={handleDragEnd}>
          {/* Asana-style table */}
          <table className="w-full border-collapse" style={{ minWidth: columns.reduce((sum, c) => sum + c.width, 0) + 100 }}>
            {/* Column Header */}
            <thead className="sticky top-0 z-20 bg-white">
              <tr className="border-b border-gray-200">
                {/* Drag + Checkbox area */}
                <th className="w-10 min-w-[40px]" />
                <th className="w-8 min-w-[32px] text-center">
                  <button
                    onClick={handleSelectAllTasks}
                    className="flex items-center justify-center w-5 h-5 mx-auto text-gray-400 hover:text-blue-600 transition-colors rounded"
                    title="Seleccionar todo"
                  >
                    {tasks.length > 0 && tasks.every(t => selectedTasks.has(t.id)) ? (
                      <FiCheckSquare size={15} className="text-blue-600" />
                    ) : selectedTasks.size > 0 ? (
                      <FiMinusSquare size={15} className="text-blue-600" />
                    ) : (
                      <FiSquare size={15} className="opacity-0 hover:opacity-100" />
                    )}
                  </button>
                </th>
                {columns.map((column) => (
                  <th
                    key={column.id}
                    style={{ width: column.width }}
                    className="relative px-3 py-2 text-left text-[12px] font-medium text-gray-500 tracking-normal border-r border-gray-100 hover:bg-gray-50/50 group cursor-default select-none"
                    onClick={(e) => {
                      if (column.id !== 'name') {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setColumnOptions({ top: rect.bottom, left: rect.left, column });
                      }
                    }}
                  >
                    <div className="flex items-center gap-1.5 truncate">
                      <span>{column.title}</span>
                    </div>
                    {/* Resize handle */}
                    <div
                      onMouseDown={(e) => handleColumnResizeStart(e, column.id)}
                      className={`absolute right-0 top-0 bottom-0 w-[3px] cursor-col-resize transition-colors z-20 ${resizingColumn === column.id ? 'bg-blue-500' : 'hover:bg-blue-400'}`}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                ))}
                {/* Add Column */}
                <th className="w-10 min-w-[40px] px-2 py-2">
                  <button
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setShowAddColumn({ top: rect.bottom, left: rect.left });
                    }}
                    className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 transition-colors"
                    title="Agregar columna"
                  >
                    <Plus size={14} />
                  </button>
                </th>
              </tr>
            </thead>

            {/* Sections */}
            {sections.map((section) => {
              const groupTasks = groupedTasks[section.id] || [];
              const isExpanded = expandedGroups[section.id] !== false;
              const completedCount = groupTasks.filter(t => t.estado === 'finalizado').length;

              return (
                <Droppable droppableId={section.id} key={section.id}>
                  {(provided, snapshot) => (
                    <tbody
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={snapshot.isDraggingOver ? 'bg-blue-50/30' : ''}
                    >
                      {/* ===== SECTION HEADER (Asana-style) ===== */}
                      <tr>
                        <td colSpan={columns.length + 3} className="px-0 py-0">
                          <div className="flex items-center gap-1 pl-2 pr-3 py-2 group border-b border-gray-200 bg-white hover:bg-gray-50/50 transition-colors">
                            {/* Toggle */}
                            <button
                              onClick={() => toggleGroup(section.id)}
                              className="p-1 rounded hover:bg-gray-100 transition-colors flex-shrink-0"
                            >
                              <ChevronDown
                                size={16}
                                className="text-gray-500 transition-transform duration-200"
                                style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                              />
                            </button>

                            {/* Section Name */}
                            {editingSectionId === section.id ? (
                              <input
                                type="text"
                                value={editingSectionName}
                                onChange={(e) => setEditingSectionName(e.target.value)}
                                onBlur={async () => {
                                  if (editingSectionName.trim() && editingSectionName !== section.nombre) {
                                    try {
                                      await updateSection(user.uid, selectedProjectId, selectedFolderId, selectedListId, section.id, { nombre: editingSectionName.trim() });
                                      toast.success('Sección renombrada');
                                    } catch (error) { toast.error('Error al renombrar sección'); }
                                  }
                                  setEditingSectionId(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') e.target.blur();
                                  if (e.key === 'Escape') { setEditingSectionId(null); setEditingSectionName(section.nombre); }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="text-[15px] font-bold bg-transparent border-b-2 border-blue-500 focus:outline-none px-1 py-0.5"
                                style={{ color: section.color || '#1e293b' }}
                                autoFocus
                              />
                            ) : (
                              <span
                                className="text-[15px] font-bold cursor-pointer hover:opacity-70 transition-opacity"
                                style={{ color: section.color || '#1e293b' }}
                                onDoubleClick={() => {
                                  setEditingSectionId(section.id);
                                  setEditingSectionName(section.nombre);
                                }}
                              >
                                {section.nombre}
                              </span>
                            )}

                            {/* Task count */}
                            <span className="text-[12px] text-gray-400 ml-1">
                              {completedCount > 0 ? `${completedCount}/${groupTasks.length}` : groupTasks.length}
                            </span>

                            {/* Section menu */}
                            <div className="relative ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSectionMenuOpen(sectionMenuOpen === section.id ? null : section.id);
                                }}
                                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                              >
                                <MoreVertical size={14} />
                              </button>

                              {sectionMenuOpen === section.id && (
                                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg z-50 border border-gray-200 py-1">
                                  <button
                                    onClick={() => {
                                      setEditingSectionId(section.id);
                                      setEditingSectionName(section.nombre);
                                      setSectionMenuOpen(null);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                                  >
                                    <Edit2 size={13} />
                                    Renombrar
                                  </button>
                                  <button
                                    onClick={async () => {
                                      setDeleteModal({
                                        isOpen: true, type: 'task',
                                        title: `¿Eliminar "${section.nombre}"?`,
                                        message: 'Las tareas se quedarán sin sección. Esta acción no se puede deshacer.',
                                        onConfirm: async () => {
                                          try {
                                            await deleteSection(user.uid, selectedProjectId, selectedFolderId, selectedListId, section.id);
                                            toast.success('Sección eliminada');
                                          } catch (error) { toast.error('Error al eliminar sección'); }
                                        }
                                      });
                                      setSectionMenuOpen(null);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                                  >
                                    <Trash2 size={13} />
                                    Eliminar sección
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <>
                          {/* ===== TASK ROWS (Asana-style) ===== */}
                          {groupTasks.map((task, index) => {
                            const isSelected = selectedTasks.has(task.id);
                            const isTaskExpanded = expandedTasks[task.id];
                            const countSubtasks = (subtareas) => {
                              if (!subtareas || subtareas.length === 0) return 0;
                              return subtareas.length;
                            };
                            const totalSubtasks = countSubtasks(task.subtareas);

                            return (
                              <React.Fragment key={task.id}>
                                <Draggable draggableId={task.id} index={index}>
                                  {(provided, snapshot) => (
                                    <tr
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      onContextMenu={(e) => handleContextMenu(e, task)}
                                      className={`group border-b border-gray-100 transition-colors duration-100
                                        ${isSelected ? 'bg-blue-50' : 'bg-white hover:bg-gray-50/70'}
                                        ${snapshot.isDragging ? 'shadow-lg bg-white ring-1 ring-blue-300 z-10' : ''}
                                        ${task.estado === 'finalizado' ? 'opacity-60' : ''}`}
                                      style={{ ...provided.draggableProps.style, height: 36 }}
                                    >
                                      {/* Drag Handle */}
                                      <td className="w-10 text-center">
                                        <div
                                          {...provided.dragHandleProps}
                                          className="cursor-grab text-transparent group-hover:text-gray-300 hover:text-gray-500 inline-block transition-colors"
                                        >
                                          <GripVertical size={14} />
                                        </div>
                                      </td>

                                      {/* Checkbox */}
                                      <td className="w-8 text-center">
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleSelectTask(task.id); }}
                                          className="flex items-center justify-center w-5 h-5 mx-auto"
                                        >
                                          {isSelected ? (
                                            <FiCheckSquare size={15} className="text-blue-600" />
                                          ) : (
                                            <FiSquare size={15} className="text-gray-300 opacity-0 group-hover:opacity-100 hover:text-gray-500 transition-all" />
                                          )}
                                        </button>
                                      </td>

                                      {/* Columns */}
                                      {columns.map((column) => (
                                        <td
                                          key={column.id}
                                          onContextMenu={column.id === 'name' ? (e) => handleContextMenu(e, task) : undefined}
                                          className={`px-3 text-sm text-gray-700 border-r border-gray-100 truncate ${column.id === 'name' ? 'py-0' : 'py-1'}`}
                                          style={{ width: column.width }}
                                        >
                                          {(() => {
                                            // ---- NAME COLUMN ----
                                            if (column.id === 'name') {
                                              return (
                                                <div className="flex items-center gap-2 h-full cursor-pointer" onClick={() => { setSelectedTaskId(task.id); setSelectedSubtaskPath([]); setShowTaskDrawer(true); }}>
                                                  {/* Completion circle */}
                                                  <CompletionCircle
                                                    estado={task.estado}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      const newStatus = task.estado === 'finalizado' ? 'planificacion' : 'finalizado';
                                                      handleStatusChange({ taskId: task.id, isSubtask: false }, newStatus);
                                                    }}
                                                  />

                                                  {/* Subtask expand toggle */}
                                                  {totalSubtasks > 0 ? (
                                                    <button
                                                      onClick={() => toggleTask(task.id)}
                                                      className="text-gray-400 hover:text-gray-600 flex-shrink-0 transition-colors"
                                                    >
                                                      {isTaskExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                    </button>
                                                  ) : (
                                                    <div className="w-[14px] flex-shrink-0" />
                                                  )}

                                                  {/* Task name - inline editable */}
                                                  <InlineTaskName
                                                    titulo={task.titulo}
                                                    isCompleted={task.estado === 'finalizado'}
                                                    onSave={async (newTitle) => {
                                                      try { await updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, task.id, { titulo: newTitle }); } catch {}
                                                    }}
                                                    onClick={() => {
                                                      setSelectedTaskId(task.id);
                                                      setSelectedSubtaskPath([]);
                                                      setShowTaskDrawer(true);
                                                    }}
                                                    onContextMenu={(e) => handleContextMenu(e, task)}
                                                  />

                                                  {/* Subtask count badge */}
                                                  {totalSubtasks > 0 && (
                                                    <span className="flex-shrink-0 text-[11px] text-gray-400 flex items-center gap-0.5">
                                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                                      </svg>
                                                      {totalSubtasks}
                                                    </span>
                                                  )}

                                                  {/* AI + Add subtask buttons */}
                                                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        setAiPopover({
                                                          taskId: task.id, initialContent: task.titulo, isSubtask: false,
                                                          position: { top: rect.bottom + 5, left: rect.left }
                                                        });
                                                      }}
                                                      className="p-0.5 text-gray-300 hover:text-indigo-500 hover:bg-indigo-50 rounded transition-all"
                                                      title="Mejorar con IA"
                                                    >
                                                      <FiZap size={13} />
                                                    </button>
                                                    <button
                                                      onClick={() => handleAddSubtask(task.id)}
                                                      className="p-0.5 text-gray-300 hover:text-blue-500 hover:bg-blue-50 rounded transition-all"
                                                      title="Agregar subtarea"
                                                    >
                                                      <Plus size={13} />
                                                    </button>
                                                  </div>
                                                </div>
                                              );
                                            }

                                            // ---- ASSIGNEE COLUMN ----
                                            if (column.id === 'assignee') {
                                              return renderAssignee(task.asignados, (e) => {
                                                e.stopPropagation();
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setUserPopoverTask({ taskId: task.id, isSubtask: false });
                                                setUserPopoverPos({ top: rect.bottom, left: rect.left });
                                              });
                                            }

                                            // ---- DATE COLUMN ----
                                            if (column.id === 'date') {
                                              const dateStr = formatDate(task.fechaVencimiento);
                                              const dateColor = getDateColor(task.fechaVencimiento);
                                              return (
                                                <span
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    setDatePopoverTask({ taskId: task.id, isSubtask: false });
                                                    setDatePopoverPos({ top: rect.bottom, left: rect.left });
                                                  }}
                                                  className={`text-[13px] cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors inline-block ${dateColor}`}
                                                >
                                                  {dateStr || <span className="text-gray-300">--</span>}
                                                </span>
                                              );
                                            }

                                            // ---- STATUS COLUMN ----
                                            if (column.id === 'status') {
                                              return (
                                                <div
                                                  onClick={(e) => {
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    setStatusPopoverTask({ taskId: task.id, isSubtask: false });
                                                    setStatusPopoverPos({ top: rect.bottom, left: rect.left });
                                                  }}
                                                  className="cursor-pointer hover:opacity-80 transition-opacity"
                                                >
                                                  <StatusTag statusKey={task.estado} />
                                                </div>
                                              );
                                            }

                                            // ---- CUSTOM COLUMNS ----
                                            return (
                                              <EditableCell
                                                task={task} column={column} isSubtask={false}
                                                user={user} selectedProjectId={selectedProjectId}
                                                selectedFolderId={selectedFolderId} selectedListId={selectedListId}
                                              />
                                            );
                                          })()}
                                        </td>
                                      ))}

                                      {/* Add Column placeholder */}
                                      <td className="w-10" />
                                    </tr>
                                  )}
                                </Draggable>

                                {/* ===== SUBTASKS ===== */}
                                {isTaskExpanded && (
                                  <tr className="bg-white">
                                    <td colSpan={columns.length + 3} className="p-0">
                                      <Droppable droppableId={`subtasks-${task.id}`} type="SUBTASK">
                                        {(provided) => (
                                          <div ref={provided.innerRef} {...provided.droppableProps} className="border-l-2 border-blue-200 ml-10">
                                            {task.subtareas && task.subtareas.map((subtask, subIndex) => (
                                              <Draggable key={subtask.id} draggableId={subtask.id} index={subIndex}>
                                                {(provided, snapshot) => (
                                                  <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    onContextMenu={(e) => handleContextMenu(e, subtask, true)}
                                                    className={`flex items-center h-[32px] border-b border-gray-100 hover:bg-gray-50/70 group/sub transition-colors
                                                      ${snapshot.isDragging ? 'bg-white shadow-md' : ''}
                                                      ${subtask.estado === 'finalizado' ? 'opacity-50' : ''}`}
                                                    style={{ ...provided.draggableProps.style }}
                                                  >
                                                    {/* Drag handle */}
                                                    <div
                                                      {...provided.dragHandleProps}
                                                      className="w-8 flex items-center justify-center text-transparent group-hover/sub:text-gray-300 cursor-grab flex-shrink-0"
                                                    >
                                                      <GripVertical size={12} />
                                                    </div>

                                                    {/* Completion circle */}
                                                    <div className="flex items-center gap-2 flex-1 min-w-0 pr-3">
                                                      <CompletionCircle
                                                        estado={subtask.estado}
                                                        size={16}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          const newStatus = subtask.estado === 'finalizado' ? 'planificacion' : 'finalizado';
                                                          handleStatusChange({ taskId: subtask.id, isSubtask: true, parentId: task.id }, newStatus);
                                                        }}
                                                      />

                                                      {/* Subtask name - inline editable */}
                                                      <InlineTaskName
                                                        titulo={subtask.titulo}
                                                        isCompleted={subtask.estado === 'finalizado'}
                                                        onSave={async (newTitle) => {
                                                          try { await updateSubtask(user.uid, selectedProjectId, selectedFolderId, selectedListId, task.id, subtask.id, { titulo: newTitle }); } catch {}
                                                        }}
                                                        onClick={() => {
                                                          setSelectedTaskId(task.id);
                                                          setSelectedSubtaskPath([subtask.id]);
                                                          setShowTaskDrawer(true);
                                                        }}
                                                        onContextMenu={(e) => handleContextMenu(e, subtask, true)}
                                                      />

                                                      {/* AI button */}
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          const rect = e.currentTarget.getBoundingClientRect();
                                                          setAiPopover({
                                                            taskId: task.id, subtaskId: subtask.id, initialContent: subtask.titulo,
                                                            isSubtask: true, position: { top: rect.bottom + 5, left: rect.left }
                                                          });
                                                        }}
                                                        className="p-0.5 opacity-0 group-hover/sub:opacity-100 text-gray-300 hover:text-indigo-500 rounded transition-all flex-shrink-0"
                                                      >
                                                        <FiZap size={12} />
                                                      </button>

                                                      {/* Assignee (subtask) */}
                                                      <div className="flex-shrink-0">
                                                        {renderAssignee(subtask.asignados, (e) => {
                                                          e.stopPropagation();
                                                          const rect = e.currentTarget.getBoundingClientRect();
                                                          setUserPopoverTask({ taskId: subtask.id, isSubtask: true, parentId: task.id });
                                                          setUserPopoverPos({ top: rect.bottom, left: rect.left });
                                                        })}
                                                      </div>

                                                      {/* Date (subtask) */}
                                                      <span
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          const rect = e.currentTarget.getBoundingClientRect();
                                                          setDatePopoverTask({ taskId: subtask.id, isSubtask: true, parentId: task.id });
                                                          setDatePopoverPos({ top: rect.bottom, left: rect.left });
                                                        }}
                                                        className={`text-[12px] cursor-pointer hover:bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0 ${getDateColor(subtask.fechaVencimiento)}`}
                                                      >
                                                        {formatDate(subtask.fechaVencimiento)}
                                                      </span>
                                                    </div>
                                                  </div>
                                                )}
                                              </Draggable>
                                            ))}
                                            {provided.placeholder}

                                            {/* Add subtask inline */}
                                            {addingSubtaskFor === task.id && (
                                              <div className="flex items-center h-[32px] pl-8 pr-3 border-b border-gray-100 bg-blue-50/30">
                                                <div className="w-4 h-4 rounded-full border border-dashed border-gray-300 flex-shrink-0 mr-2" />
                                                <input
                                                  type="text"
                                                  value={newSubtaskTitle}
                                                  autoFocus
                                                  onChange={(e) => setNewSubtaskTitle(e.target.value)}
                                                  onKeyDown={(e) => e.key === 'Enter' && handleSaveSubtask(task.id)}
                                                  onBlur={() => newSubtaskTitle.trim() ? handleSaveSubtask(task.id) : setAddingSubtaskFor(null)}
                                                  className="flex-1 text-[13px] bg-transparent border-none focus:outline-none focus:ring-0 placeholder-gray-400"
                                                  placeholder="Agregar subtarea..."
                                                />
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </Droppable>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                          {provided.placeholder}

                          {/* Asana-style "Add task" row */}
                          <InlineTaskAdd
                            sectionId={section.id}
                            onSave={(taskData) => createTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, taskData)}
                          />
                        </>
                      )}
                      {!isExpanded && provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              );
            })}
          </table>

          {/* Add Section */}
          <div className="mt-2 mb-8 px-3">
            {showNewSection ? (
              <div className="flex items-center gap-2 py-2">
                <input
                  type="text"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && newSectionName.trim()) {
                      try {
                        await createSection(user.uid, selectedProjectId, selectedFolderId, selectedListId, {
                          nombre: newSectionName.trim(), color: '#6b7280', orden: sections.length + 1
                        });
                        toast.success('Sección creada');
                        setNewSectionName('');
                        setShowNewSection(false);
                      } catch (error) { toast.error('Error al crear sección'); }
                    }
                    if (e.key === 'Escape') { setShowNewSection(false); setNewSectionName(''); }
                  }}
                  className="text-[15px] font-bold bg-transparent border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none px-1 py-1 text-gray-700 w-64"
                  placeholder="Nombre de la sección"
                  autoFocus
                />
                <button onClick={() => { setShowNewSection(false); setNewSectionName(''); }} className="p-1 text-gray-400 hover:text-gray-600">
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowNewSection(true)}
                className="flex items-center gap-2 px-2 py-2 text-[13px] text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors"
              >
                <Plus size={14} /> Agregar sección
              </button>
            )}
          </div>
        </DragDropContext>
      </div>

      {/* Popovers */}
      {showAddColumn && (
        <AddColumnPopover position={showAddColumn} onClose={() => setShowAddColumn(null)} onAddColumn={handleAddColumn} />
      )}

      {columnOptions && (
        <ColumnOptionsPopover
          position={{ top: columnOptions.top, left: columnOptions.left }}
          column={columnOptions?.column}
          onClose={() => setColumnOptions(null)}
          onAction={handleColumnAction}
        />
      )}

      {statusPopoverTask && statusPopoverPos && (
        <StatusPopover
          currentStatus={
            statusPopoverTask.isSubtask
              ? tasks.find(t => t.id === statusPopoverTask.parentId)?.subtareas?.find(s => s.id === statusPopoverTask.taskId)?.estado
              : tasks.find(t => t.id === statusPopoverTask.taskId)?.estado
          }
          onSelect={(newStatus) => handleStatusChange(statusPopoverTask, newStatus)}
          position={statusPopoverPos}
          onClose={() => { setStatusPopoverTask(null); setStatusPopoverPos(null); }}
        />
      )}

      {datePopoverTask && datePopoverPos && (
        <DatePickerPopover
          date={
            datePopoverTask.isSubtask
              ? tasks.find(t => t.id === datePopoverTask.parentId)?.subtareas?.find(s => s.id === datePopoverTask.taskId)?.fechaVencimiento
              : tasks.find(t => t.id === datePopoverTask.taskId)?.fechaVencimiento
          }
          onDateChange={(newDate) => handleDateChange(datePopoverTask, newDate)}
          position={datePopoverPos}
          onClose={() => { setDatePopoverTask(null); setDatePopoverPos(null); }}
        />
      )}

      {userPopoverTask && userPopoverPos && (
        <UserSelectPopover
          users={users}
          position={userPopoverPos}
          currentAssignee={
            userPopoverTask.isSubtask
              ? tasks.find(t => t.id === userPopoverTask.parentId)?.subtareas?.find(s => s.id === userPopoverTask.taskId)?.asignados?.[0]
              : tasks.find(t => t.id === userPopoverTask.taskId)?.asignados?.[0]
          }
          onSelect={(email) => handleAssigneeChange(userPopoverTask, email)}
          onClose={() => { setUserPopoverTask(null); setUserPopoverPos(null); }}
        />
      )}

      {contextMenu && (
        <TaskContextMenu
          position={{ x: contextMenu.x, y: contextMenu.y }}
          task={contextMenu.task}
          onClose={() => setContextMenu(null)}
          onAction={(action) => handleContextMenuAction(action, contextMenu.task)}
        />
      )}

      {aiPopover && (
        <AIPopover
          initialContent={aiPopover.initialContent}
          position={aiPopover.position}
          targetName="título"
          onClose={() => setAiPopover(null)}
          onInsert={async (newContent) => {
            try {
              if (aiPopover.isSubtask) {
                await updateSubtask(user.uid, selectedProjectId, selectedFolderId, selectedListId, aiPopover.taskId, aiPopover.subtaskId, { titulo: newContent });
              } else {
                await updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, aiPopover.taskId, { titulo: newContent });
              }
              toast.success('Título actualizado con IA');
            } catch (error) {
              console.error(error);
              toast.error('Error al actualizar título');
            }
            setAiPopover(null);
          }}
        />
      )}

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDelete}
        title={deleteModal.title}
        message={deleteModal.message}
        itemType={deleteModal.type === 'task' ? 'tarea' : 'subtarea'}
      />

      {/* Bulk Actions Toolbar (Asana-style floating bar) */}
      {selectedTasks.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 bg-gray-900 text-white px-4 py-2 rounded-xl shadow-2xl border border-gray-700">
          <span className="text-sm font-medium text-blue-400 mr-2">
            {selectedTasks.size} seleccionada{selectedTasks.size > 1 ? 's' : ''}
          </span>
          <div className="w-px h-5 bg-gray-600" />

          {/* Status */}
          <div className="relative">
            <button
              onClick={() => { setBulkStatusOpen(!bulkStatusOpen); setBulkPriorityOpen(false); setBulkAssigneeOpen(false); setBulkDateOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FiCheckCircle size={14} /> Estado
            </button>
            {bulkStatusOpen && (
              <div className="absolute bottom-full mb-2 left-0 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[160px]">
                {[
                  { key: 'planificacion', label: 'Planificación', color: 'bg-gray-400' },
                  { key: 'en-curso', label: 'En Curso', color: 'bg-blue-500' },
                  { key: 'finalizado', label: 'Finalizado', color: 'bg-green-500' },
                ].map(s => (
                  <button key={s.key} onClick={() => handleBulkStatusChange(s.key)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                    <div className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Priority */}
          <div className="relative">
            <button
              onClick={() => { setBulkPriorityOpen(!bulkPriorityOpen); setBulkStatusOpen(false); setBulkAssigneeOpen(false); setBulkDateOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FiFlag size={14} /> Prioridad
            </button>
            {bulkPriorityOpen && (
              <div className="absolute bottom-full mb-2 left-0 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[140px]">
                {[
                  { key: 'alta', label: 'Alta', color: 'bg-red-500' },
                  { key: 'media', label: 'Media', color: 'bg-yellow-500' },
                  { key: 'baja', label: 'Baja', color: 'bg-green-500' },
                ].map(p => (
                  <button key={p.key} onClick={() => handleBulkPriorityChange(p.key)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                    <div className={`w-2.5 h-2.5 rounded-full ${p.color}`} />
                    {p.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Assignee */}
          <div className="relative">
            <button
              onClick={() => { setBulkAssigneeOpen(!bulkAssigneeOpen); setBulkStatusOpen(false); setBulkPriorityOpen(false); setBulkDateOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FiUserPlus size={14} /> Asignar
            </button>
            {bulkAssigneeOpen && (
              <div className="absolute bottom-full mb-2 left-0 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[200px] max-h-[200px] overflow-y-auto">
                <button onClick={() => handleBulkAssigneeChange(null)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 transition-colors">
                  <FiUser size={14} /> Sin asignar
                </button>
                {users.map(u => (
                  <button key={u.email} onClick={() => handleBulkAssigneeChange(u.email)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                    {u.photoURL ? (
                      <img src={u.photoURL} alt="" className="w-5 h-5 rounded-full" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center">
                        {(u.displayName || u.email).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="truncate">{u.displayName || u.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Date */}
          <div className="relative">
            <button
              onClick={() => { setBulkDateOpen(!bulkDateOpen); setBulkStatusOpen(false); setBulkPriorityOpen(false); setBulkAssigneeOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FiCalendar size={14} /> Fecha
            </button>
            {bulkDateOpen && (
              <div className="absolute bottom-full mb-2 left-0 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[160px]">
                {[
                  { label: 'Hoy', days: 0 },
                  { label: 'Mañana', days: 1 },
                  { label: 'Próxima semana', days: 7 },
                  { label: 'En 2 semanas', days: 14 },
                  { label: 'En 1 mes', days: 30 },
                  { label: 'Sin fecha', days: null },
                ].map(d => (
                  <button key={d.label} onClick={() => {
                    if (d.days === null) handleBulkDateChange(null);
                    else { const date = new Date(); date.setDate(date.getDate() + d.days); handleBulkDateChange(date.toISOString()); }
                  }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors text-left">
                    {d.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="w-px h-5 bg-gray-600" />

          <button onClick={handleBulkDuplicate}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors">
            <FiCopy size={14} /> Duplicar
          </button>

          <button onClick={handleBulkDelete}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-red-400 hover:bg-red-900/30 transition-colors">
            <FiTrash2 size={14} /> Eliminar
          </button>

          <button onClick={() => setSelectedTasks(new Set())}
            className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-gray-700 transition-colors ml-1">
            <FiX size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default TaskListViewImproved;
